import { Airline } from "@/types/flights/airline"
import { FlightSliceApi } from "@/types/flights/flight-slice-api"
import last from "lodash/last"
import uniqBy from "lodash/uniqBy"

import { getDayObject, nightsBetweenDayObjects } from "@/lib/date-utils"
import {
  arrivalWallClock,
  departureWallClock,
  stopsToText,
} from "@/lib/flight-utils"
import { Locale } from "@/lib/locale-hooks"
import { assertExists } from "@/lib/utils/assertions"
import { AirlinesImg } from "./airlines-img"
import { FlightSourceBadge } from "./flight-option-badges"

interface FlightSliceCardHeaderProps {
  flightSlice: FlightSliceApi
  isReturn?: boolean
  minimal?: boolean
  sliceLabel?: string
  sourceType?: string
}

export function FlightSliceCardHeader({
  flightSlice,
  isReturn = false,
  minimal = false,
  sliceLabel,
  sourceType,
}: FlightSliceCardHeaderProps) {
  const formatFlightDuration = Locale.useFormatFlightDuration()
  const formatWeekdayMonthDay = Locale.useFormatWeekdayMonthDay()
  const formatHourMinute = Locale.useFormatHourMinute()

  const legs = flightSlice.flight_legs

  const firstLeg = legs[0]
  const lastLeg = assertExists(last(legs))

  const departure = departureWallClock(firstLeg)
  const arrival = arrivalWallClock(lastLeg)

  const initialDepartureDay = getDayObject(departure.at, departure.timeZone)

  const finalArrivalDay = getDayObject(arrival.at, arrival.timeZone)

  const finalArrivalDayExp = nightsBetweenDayObjects(
    initialDepartureDay,
    finalArrivalDay
  )

  const headerDate = formatWeekdayMonthDay(departure.at, departure.timeZone)
  const sliceDepartureTime = formatHourMinute(departure.at, departure.timeZone)
  const sliceArrivalTime = formatHourMinute(arrival.at, arrival.timeZone)

  const airlines = uniqBy(
    legs
      .map((leg) =>
        leg.properties.airlines.find((a) => a.iata === leg.airline_iata)
      )
      .filter((a) => a != null) as Airline[],
    "iata"
  )

  return (
    <>
      <div className="mt-0.5 mr-4 shrink-0">
        <AirlinesImg airlines={airlines} />
      </div>
      <div className="no-translate @container flex-1">
        {minimal ? (
          <div className="mt-1 font-semibold">
            {sliceLabel ? (
              <span>{sliceLabel}</span>
            ) : isReturn ? (
              <span>Return</span>
            ) : (
              <span>Departure</span>
            )}
            <span className="mx-2 hidden @[12rem]:inline">·</span>
            <br className="@[12rem]:hidden" />
            {headerDate}
          </div>
        ) : (
          <div className="flex flex-wrap gap-y-2.5">
            <div className="w-full font-semibold @[15rem]:w-2/5 @sm:w-[24.5%]">
              <div>{headerDate}</div>
              <div className="text-xs">
                {sliceLabel ??
                  (isReturn ? <span>Return</span> : <span>Departure</span>)}
              </div>
            </div>
            <div className="w-full pr-1.5 @[15rem]:w-3/5 @sm:w-[39.5%]">
              <div className="font-semibold whitespace-nowrap">
                {sliceDepartureTime}-{sliceArrivalTime}
                {finalArrivalDayExp > 0 && <sup>+{finalArrivalDayExp}</sup>}
              </div>
              <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted">
                <span className="truncate">
                  {airlines.map((x) => x.name).join(", ")}
                </span>
                <FlightSourceBadge sourceType={sourceType} />
              </div>
            </div>
            <div className="w-3/5 max-w-[6em] @[15rem]:w-2/5 @[15rem]:max-w-none @sm:w-[18%]">
              <div className="no-translate font-semibold">
                {formatFlightDuration(flightSlice.properties.duration)}
              </div>
              <div className="text-xs text-muted">
                {firstLeg.departure_airport.code}-{lastLeg.arrival_airport.code}
              </div>
            </div>
            <div className="w-2/5 @[15rem]:w-3/5 @sm:w-[18%]">
              <div className="font-semibold">
                {stopsToText(flightSlice.stops)}
              </div>
              <div className="text-xs text-muted">
                {flightSlice.layovers.map((x) => x.airport.code).join(", ")}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
